import type { FlightFrame, FlightState, Vec3 } from '../types';
import { cloneVec3 } from '../physics/vec3Math';

/**
 * Records flight frames for replay, shot tracer, and analytics.
 */
export class FlightRecorder {
  private frames: FlightFrame[] = [];

  record(state: FlightState, phase: 'flight' | 'landing' = 'flight'): void {
    // state.spinRate is already decayed by the flight ODE's velocity-dependent
    // spin decay derivative (BallFlightModel.ts:158). No additional decay here.
    this.frames.push({
      time: state.time,
      position: cloneVec3(state.position),
      velocity: cloneVec3(state.velocity),
      spinRate: state.spinRate,
      phase,
    });
  }

  recordGround(time: number, position: Vec3, velocity: Vec3): void {
    this.frames.push({
      time,
      position: cloneVec3(position),
      velocity: cloneVec3(velocity),
      spinRate: 0,
      phase: 'ground',
    });
  }

  getFrames(): FlightFrame[] {
    return this.frames;
  }

  getFrameCount(): number {
    return this.frames.length;
  }

  getFrameAtTime(time: number): FlightFrame | null {
    if (this.frames.length === 0) return null;

    // Binary search for the two frames surrounding the time
    let lo = 0;
    let hi = this.frames.length - 1;

    if (time <= this.frames[lo].time) return this.frames[lo];
    if (time >= this.frames[hi].time) return this.frames[hi];

    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (this.frames[mid].time <= time) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    // Interpolate between lo and hi
    const f0 = this.frames[lo];
    const f1 = this.frames[hi];
    const t = (time - f0.time) / (f1.time - f0.time);

    return {
      time,
      position: {
        x: f0.position.x + (f1.position.x - f0.position.x) * t,
        y: f0.position.y + (f1.position.y - f0.position.y) * t,
        z: f0.position.z + (f1.position.z - f0.position.z) * t,
      },
      velocity: {
        x: f0.velocity.x + (f1.velocity.x - f0.velocity.x) * t,
        y: f0.velocity.y + (f1.velocity.y - f0.velocity.y) * t,
        z: f0.velocity.z + (f1.velocity.z - f0.velocity.z) * t,
      },
      spinRate: f0.spinRate + (f1.spinRate - f0.spinRate) * t,
    };
  }

  getTotalTime(): number {
    if (this.frames.length === 0) return 0;
    return this.frames[this.frames.length - 1].time;
  }

  clear(): void {
    this.frames = [];
  }
}
